/*
  Global theme JavaScript.
  Keep this file small: component behaviour belongs in {% javascript %} tags
  inside the section, block or snippet that owns the markup.
*/

document.documentElement.classList.remove('no-js');

/*
  The cart layer. Every cart write in this theme goes through it — adding today,
  removing and re-counting later — so that there is one place that knows how to
  talk to Shopify and one event everyone else listens to.

  Two things it does on purpose:

  1. It asks the Cart API to re-render the sections that show the cart
     (`sections: 'header,cart-drawer'`) and takes the new count out of that
     markup. The count is server-rendered either way, so nothing here has to
     know how a cart total is formatted or what else the header might grow to
     show. That is the Section Rendering API doing the job the theme guidelines
     ask of it.
  2. It publishes `cart:updated` with the cart payload and the rendered
     sections. A drawer, a line-item row or a free-shipping bar can subscribe
     without this file learning they exist.
*/
const Theme = {
  routes: window.Shopify && window.Shopify.routes ? window.Shopify.routes : { root: '/' },

  /*
    The sections re-rendered on every cart write. The header carries the count
    and is on every page; everything else declares itself with a
    data-cart-section attribute — the drawer in layout/theme.liquid, the cart
    page in its own template. Each subscriber then picks its own markup out of
    the payload.

    Collected from the DOM rather than listed here, so the list cannot fall out
    of step with what is actually on the page: on /cart the drawer is not
    rendered at all, and asking Shopify to re-render it would be work spent on
    markup nobody is going to read.
  */
  cartSections() {
    const ids = new Set(['header']);

    document.querySelectorAll('[data-cart-section]').forEach((node) => {
      if (node.dataset.cartSection) ids.add(node.dataset.cartSection);
    });

    return Array.from(ids);
  },

  async fetchCart() {
    const response = await fetch(`${this.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  },

  async cartWrite(endpoint, payload) {
    const response = await fetch(`${this.routes.root}cart/${endpoint}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: this.cartSections().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Shopify puts the human-readable reason in `description`, e.g. sold out.
      throw new Error(data.description || data.message || `Cart request failed with status ${response.status}`);
    }

    this.renderCartSections(data.sections);
    this.publish('cart:updated', { cart: data, sections: data.sections });

    return data;
  },

  /*
    Re-render the cart sections without writing anything to the cart.

    Needed after a refusal. Shopify answers 422 when a quantity exceeds stock,
    but it still puts the available maximum in — the number on screen then
    disagrees with the cart, and a checkout against a stale total is the one
    mistake here that costs money.

    Publishes cart:refreshed rather than cart:updated: the drawer opens itself
    on cart:updated, and it has no business sliding over an error message.

    The sections are requested against the current address — otherwise Shopify
    builds them for the home page and /cart receives an empty cart. Ones that
    fail to build arrive as null with a status of 200, which is why the
    subscribers check the type.

    No Accept: application/json header, and that is not an oversight. On /cart
    it switches Shopify to returning the cart object itself, and the contents of
    /cart.js arrive instead of section markup — silently, with a status of 200.
    The Section Rendering API already answers JSON when `sections` is set.
  */
  async refreshCartSections() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.cartSections().join(','));

    const response = await fetch(url);
    if (!response.ok) return null;

    const sections = await response.json();

    this.renderCartSections(sections);
    this.publish('cart:refreshed', { sections });

    return sections;
  },

  addToCart(items) {
    return this.cartWrite('add', { items });
  },

  changeCartLine(id, quantity) {
    return this.cartWrite('change', { id, quantity });
  },

  /*
    Takes the count out of the freshly rendered section rather than out of the
    JSON, so the markup stays Liquid's business. Every [data-cart-count] on the
    page is updated, which covers a second cart link if one ever appears.
  */
  renderCartSections(sections) {
    if (!sections) return;

    // A section that failed to build arrives as null with a status of 200.
    const markup = sections.header;
    if (typeof markup !== 'string') return;

    const fresh = new DOMParser()
      .parseFromString(markup, 'text/html')
      .querySelector('[data-cart-count]');
    if (!fresh) return;

    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = fresh.textContent;
    });
  },

  publish(event, detail) {
    document.dispatchEvent(new CustomEvent(event, { detail }));
  },
};

window.Theme = Theme;

/*
  A one-shot reveal on scroll.

  Here rather than in a block because two blocks need it already (tech-specs and
  performance-profile) and more will: motion in this theme is on by default, not
  on request. What they share is exactly this — "show yourself once the reader
  has scrolled to you, then stop watching"; what to show is each block's own
  business.

  Two attributes, not one. `data-armed` is set the moment the element connects:
  it is permission to hide the content, and without JS it never appears at all —
  CSS then draws everything in its final state and the animation simply does not
  exist. `data-revealed` arrives when the element is actually seen and starts
  the transitions. That split is what keeps the section working rather than
  empty when JS is off.

  The observer disconnects on the first intersection: the section appears once,
  and holding a scroll subscription for the rest of the session buys nothing.
  The 15% bottom margin asks it to appear once the block is properly on screen
  rather than by its edge.
*/
Theme.revealOnScroll = function revealOnScroll(element, onReveal) {
  if (element.hasAttribute('data-armed')) return;

  element.setAttribute('data-armed', '');

  const reveal = () => {
    if (element.hasAttribute('data-revealed')) return;
    if (typeof onReveal === 'function') onReveal();
    element.setAttribute('data-revealed', '');
  };

  if (!('IntersectionObserver' in window)) {
    reveal();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      reveal();
    },
    { rootMargin: '0px 0px -15% 0px' }
  );

  observer.observe(element);
};

/*
  The width of a plate's own line of text, written onto it as --plate-width.

  Here rather than in a component because two of them need it — the product
  page's spec table (blocks/tech-specs.liquid) and the home page's feature
  section (sections/product-feature.liquid) — and both need it for the same
  reason: in the first frame of the reveal the black bar hugs its text, and
  "the width of the text" cannot be said in CSS. clip-path wants a length, not
  a keyword, and fit-content does not interpolate to 100%.

  A Range over the contents gives the width of the line without a single change
  to the markup; the bar's own padding is added to it, and out comes exactly the
  rectangle that was drawn.

  The measurement is taken again once the fonts arrive: the bar is set in
  Helvetica LT Pro, and a value read before it loads would be the fallback
  face's. After the reveal it no longer matters — the clip is open by then —
  but before it, it has to be right. Callers therefore do not have to know
  about document.fonts at all.
*/
Theme.measurePlate = function measurePlate(plate) {
  if (!plate) return;

  const measure = () => {
    const range = document.createRange();
    range.selectNodeContents(plate);
    const text = range.getBoundingClientRect().width;
    if (!text) return;

    const styles = getComputedStyle(plate);
    const padding = parseFloat(styles.paddingInlineStart) + parseFloat(styles.paddingInlineEnd);

    plate.style.setProperty('--plate-width', `${Math.ceil(text + padding)}px`);
  };

  measure();

  if (document.fonts) document.fonts.ready.then(measure);
};

/*
  Klaviyo.

  The second external service this theme talks to, and it gets the same
  treatment as the cart above: one place that knows the endpoint, the contract
  version and the shape of a request, so the two sections that use it
  (footer.liquid, main-product.liquid) are left with nothing but their own
  interface — spinner, message, focus.

  Only Klaviyo's *client* endpoints are used. They authenticate with the public
  key alone, which is exactly right for a theme: every line of this file is
  served to the browser, so a private key here would be a published secret.
  Both endpoints answer 202 with an empty body — there is nothing to parse on
  success, and any body that does arrive is an error.

  Nothing from Klaviyo's own onsite script (klaviyo.js) is used or expected.
  The Shopify app should be installed for the catalog and order sync, but its
  app embed stays off: the whole point of writing this by hand is that the
  storefront loads no third-party JavaScript. `Theme.klaviyo` rather than
  `window.klaviyo` for the same reason — the latter is that script's global,
  and the two would collide if the embed were ever switched on.
*/
Theme.klaviyo = {
  endpoint: 'https://a.klaviyo.com/client',

  /*
    The date of the contract we wrote against, not "the newest version".
    Klaviyo keeps old revisions working, so this moves only when someone has
    read the changelog and the code below — never on a schedule.
  */
  revision: '2026-07-15',

  /*
    Both calls funnel through here so the header set, the error surface and the
    logging rule exist once.

    On failure the reason Klaviyo sends back goes to the console and nowhere
    else. The three that actually happen on first setup — a wrong public key, a
    wrong list id, a variant the catalog has not synced yet — are indistinguishable
    to the shopper and mean nothing to them, so the interface says only that it
    did not work (see the section's error copy) while the console says which of
    the three it was. The email address is never logged: it is someone else's,
    and a storefront console is read by anyone and swept up by error reporting.
  */
  async post(path, key, body) {
    let response;

    try {
      response = await fetch(`${this.endpoint}/${path}/?company_id=${encodeURIComponent(key)}`, {
        method: 'POST',
        headers: {
          revision: this.revision,
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify(body),
      });
    } catch (error) {
      // Offline, DNS, a blocked third-party domain. No retry: a shopper who
      // has no connection is not helped by waiting twice as long for the same
      // failure, and the message already invites them to try again.
      console.error('[klaviyo] request failed', error);
      throw error;
    }

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      console.error(`[klaviyo] ${response.status}`, detail);
      throw new Error(`Klaviyo responded ${response.status}`);
    }

    return response;
  },

  /*
    Newsletter signup. `source` is free text that Klaviyo files with the consent
    record and shows the merchant as "where this profile came from" — passing it
    per form is what keeps a second signup point, whenever one appears, from
    being indistinguishable from this one in the reports.

    Consent is stated outright rather than left to the list's default, so the
    theme's behaviour does not change under it when someone flips a setting in
    the Klaviyo admin. Whether the address then needs confirming by email is the
    list's business — with double opt-in on (which this store uses) Klaviyo
    sends the confirmation and holds the profile back until it is clicked, which
    is why the success copy talks about the inbox rather than thanking them for
    subscribing.
  */
  subscribe(key, listId, email, source) {
    return this.post('subscriptions', key, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    });
  },

  /*
    Back in stock. Deliberately takes no list: being told once that a size
    returned is not consent to be marketed to, and quietly folding these
    addresses into the newsletter list would be collecting that consent without
    asking for it.

    The id is Klaviyo's catalog address for a variant —
    "integrationType:::catalogId:::externalId" — and for Shopify's own
    integration the first two parts are fixed. The last is the plain numeric
    variant id, which the product page already has. It resolves only once the
    Klaviyo app has synced the catalog; before that this call answers with a
    "variant not found" that looks exactly like a bug in here, which is why
    docs/klaviyo.md lists it as the first thing to check.

    EMAIL only: the popup collects no phone number, and naming a channel the
    request has no address for is rejected outright.
  */
  backInStock(key, email, variantId) {
    return this.post('back-in-stock-subscriptions', key, {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: email } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
        },
      },
    });
  },
};

/*
  Cookie banner button order.

  Shopify injects its consent banner into <body> at runtime with the buttons in
  the order manage → accept → decline; the comp puts accept last, on a row of
  its own. snippets/privacy-banner.liquid can draw that with `order` alone, but
  then the tab order no longer matches what is on screen. Moving the node keeps
  the two in step, and the attribute it leaves behind switches the CSS fallback
  off so the swap is never applied twice.

  It lives here rather than in the snippet because a snippet's {% javascript %}
  is only bundled for sections, and this snippet is rendered from the layout.
*/
function fixPrivacyBannerButtonOrder() {
  const buttons = document.querySelector('#shopify-pc__banner .shopify-pc__banner__btns');

  if (!buttons) {
    return false;
  }

  if (!buttons.hasAttribute('data-order-fixed')) {
    const accept = buttons.querySelector('.shopify-pc__banner__btn-accept');
    const decline = buttons.querySelector('.shopify-pc__banner__btn-decline');

    if (accept && decline) {
      buttons.insertBefore(decline, accept);
    }

    buttons.setAttribute('data-order-fixed', '');
  }

  return true;
}

if (!fixPrivacyBannerButtonOrder()) {
  /*
    The banner script is async, so it may land before or after this file. Watch
    <body>'s direct children only — that is where the banner is appended — and
    stop as soon as it shows up. If Shopify ever re-renders it afterwards the
    CSS fallback still gets the layout right.
  */
  const bannerObserver = new MutationObserver(() => {
    if (fixPrivacyBannerButtonOrder()) {
      bannerObserver.disconnect();
    }
  });

  bannerObserver.observe(document.body, { childList: true });
}
